import { useEffect, useRef } from 'react';

/**
 * Animated vector background for the login page: a stylized city street network with street beacons that glow
 * and pulse, and small data packets travelling from beacons to gateway hubs ("connecting dots").
 * Canvas 2D: static streets are pre-rendered once per resize; each frame draws only beacons and packets.
 * Honors prefers-reduced-motion (single static frame) and pauses while the tab is hidden.
 */
interface Palette {
  street: string;
  avenue: string;
  beaconWarm: string;
  beaconCool: string;
  hub: string;
  packet: string;
}

interface Beacon {
  x: number;
  y: number;
  phase: number;
  speed: number;
  warm: boolean;
  hub: number;
}
interface Packet {
  from: Beacon;
  t: number;
  speed: number;
}

const cssVar = (n: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();
const palette = (): Palette => ({
  street: cssVar('--bg-street'),
  avenue: cssVar('--bg-avenue'),
  beaconWarm: cssVar('--brand-warm'),
  beaconCool: cssVar('--brand-cool'),
  hub: cssVar('--brand-cool'),
  packet: cssVar('--bg-packet'),
});

/** Seeded PRNG so the city looks the same on every visit. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildCity(w: number, h: number) {
  const r = rng(20250925);
  const streets: [number, number, number, number][] = [];
  const avenues: { x0: number; y0: number; cx: number; cy: number; x1: number; y1: number }[] = [];
  const angle = -0.21; // rotated grid for a less rigid look
  const cos = Math.cos(angle),
    sin = Math.sin(angle);
  const span = Math.hypot(w, h);
  const rot = (x: number, y: number): [number, number] => [
    w / 2 + x * cos - y * sin,
    h / 2 + x * sin + y * cos,
  ];
  const block = Math.max(70, Math.min(120, span / 18));
  for (let k = -span / 2; k <= span / 2; k += block * (0.8 + r() * 0.5)) {
    if (r() < 0.85) streets.push([...rot(-span / 2, k), ...rot(span / 2, k)]);
    if (r() < 0.85) streets.push([...rot(k, -span / 2), ...rot(k, span / 2)]);
  }
  for (let i = 0; i < 3; i++) {
    avenues.push({
      x0: -50,
      y0: h * (0.15 + r() * 0.7),
      cx: w * (0.3 + r() * 0.4),
      cy: h * r(),
      x1: w + 50,
      y1: h * (0.15 + r() * 0.7),
    });
  }
  const hubs = Array.from({ length: 4 }, () => ({ x: w * (0.15 + r() * 0.7), y: h * (0.15 + r() * 0.7) }));
  const beacons: Beacon[] = [];
  const nearestHub = (x: number, y: number) => {
    let best = 0,
      bd = Infinity;
    hubs.forEach((hb, i) => {
      const d = (hb.x - x) ** 2 + (hb.y - y) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    return best;
  };
  const addBeacon = (x: number, y: number) => {
    if (x < -10 || y < -10 || x > w + 10 || y > h + 10) return;
    beacons.push({
      x,
      y,
      phase: r() * Math.PI * 2,
      speed: 0.6 + r() * 0.9,
      warm: r() < 0.8,
      hub: nearestHub(x, y),
    });
  };
  const spacing = 46;
  for (const [x0, y0, x1, y1] of streets) {
    const len = Math.hypot(x1 - x0, y1 - y0);
    for (let d = r() * spacing; d < len; d += spacing)
      if (r() < 0.55) addBeacon(x0 + ((x1 - x0) * d) / len, y0 + ((y1 - y0) * d) / len);
  }
  for (const a of avenues) {
    for (let t = 0; t <= 1; t += 0.025) {
      const x = (1 - t) ** 2 * a.x0 + 2 * (1 - t) * t * a.cx + t * t * a.x1;
      const y = (1 - t) ** 2 * a.y0 + 2 * (1 - t) * t * a.cy + t * t * a.y1;
      addBeacon(x, y);
    }
  }
  return { streets, avenues, hubs, beacons: beacons.slice(0, 700) };
}

export function CityBackground({ theme }: { theme: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let colors = palette();
    let city = buildCity(1, 1);
    let backdrop: HTMLCanvasElement | null = null;
    let raf = 0;
    let last = 0;
    const packets: Packet[] = [];
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    const drawBackdrop = (w: number, h: number) => {
      backdrop = document.createElement('canvas');
      backdrop.width = w * dpr;
      backdrop.height = h * dpr;
      const b = backdrop.getContext('2d')!;
      b.scale(dpr, dpr);
      b.lineCap = 'round';
      b.strokeStyle = colors.street;
      b.lineWidth = 1;
      for (const [x0, y0, x1, y1] of city.streets) {
        b.beginPath();
        b.moveTo(x0, y0);
        b.lineTo(x1, y1);
        b.stroke();
      }
      b.strokeStyle = colors.avenue;
      b.lineWidth = 3;
      for (const a of city.avenues) {
        b.beginPath();
        b.moveTo(a.x0, a.y0);
        b.quadraticCurveTo(a.cx, a.cy, a.x1, a.y1);
        b.stroke();
      }
    };

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = canvas;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      city = buildCity(w, h);
      drawBackdrop(w, h);
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (now - last < 33) return; // ~30 fps is plenty for ambient motion
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      draw(now / 1000, dt);
    };

    const draw = (t: number, dt: number) => {
      const w = canvas.clientWidth,
        h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      if (backdrop) ctx.drawImage(backdrop, 0, 0, w, h);
      // beacons: glow + core
      for (const l of city.beacons) {
        const a = reduced ? 0.7 : 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * l.speed + l.phase));
        const c = l.warm ? colors.beaconWarm : colors.beaconCool;
        ctx.globalAlpha = a * 0.22;
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(l.x, l.y, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = a;
        ctx.beginPath();
        ctx.arc(l.x, l.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      // hubs (gateways): pulsing rings
      ctx.strokeStyle = colors.hub;
      for (const [i, hb] of city.hubs.entries()) {
        const p = reduced ? 0.5 : (t * 0.35 + i * 0.25) % 1;
        ctx.globalAlpha = 0.5 * (1 - p);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(hb.x, hb.y, 6 + p * 34, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = colors.hub;
        ctx.beginPath();
        ctx.arc(hb.x, hb.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      // packets: beacon → hub with a fading connecting line
      if (!reduced) {
        if (packets.length < 36 && Math.random() < 0.5 && city.beacons.length) {
          packets.push({
            from: city.beacons[Math.floor(Math.random() * city.beacons.length)]!,
            t: 0,
            speed: 0.25 + Math.random() * 0.35,
          });
        }
        for (let i = packets.length - 1; i >= 0; i--) {
          const pk = packets[i]!;
          pk.t += pk.speed * dt;
          if (pk.t >= 1) {
            packets.splice(i, 1);
            continue;
          }
          const hb = city.hubs[pk.from.hub]!;
          const x = pk.from.x + (hb.x - pk.from.x) * pk.t;
          const y = pk.from.y + (hb.y - pk.from.y) * pk.t;
          ctx.globalAlpha = 0.16 * (1 - pk.t);
          ctx.strokeStyle = colors.hub;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(pk.from.x, pk.from.y);
          ctx.lineTo(hb.x, hb.y);
          ctx.stroke();
          ctx.globalAlpha = 0.95;
          ctx.fillStyle = colors.packet;
          ctx.beginPath();
          ctx.arc(x, y, 2.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden && !reduced) raf = requestAnimationFrame(frame);
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (reduced) draw(0, 0);
    else raf = requestAnimationFrame(frame);
    // theme switch: refresh colors and the pre-rendered streets
    colors = palette();
    drawBackdrop(canvas.clientWidth, canvas.clientHeight);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [theme]);

  return <canvas ref={ref} className="city-bg" aria-hidden />;
}
